import { useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { Button, Loader, Select, Text } from '@kula-ai/components';
import { Plus } from '@kula-ai/icons-core';
import { useChatSubmit } from '../../../hooks';
import { useAgentForgeStore } from '../../../stores';
import type { ClarifyBlockProps } from './ClarifyBlock.types';
import { toJobSelectOption } from './jobOption';

// A "create new …" option is an action, not a choice — it becomes the CTA
// under the dropdown rather than a row inside it.
const isCreateOption = (label: string): boolean =>
  /^create\b/i.test(label.trim());

// A cancel-ish option means "do nothing" — that must not cost an LLM turn.
// The click dismisses the card locally; the agent's workflow already ended
// when the form was emitted, so nothing waits on an answer server-side.
const isCancelOption = (value: string, label: string): boolean =>
  /^(cancel|dismiss|never ?mind|no,? (thanks|cancel))$/i.test(value.trim()) ||
  /^(cancel|dismiss|never ?mind)$/i.test(label.trim());

// Single-choice picker emitted by the backend's propose_clarify tool. Picking
// an option submits the chip's *pending* template with the value substituted
// in, so the next agent turn already has the missing ID.
export const ClarifyBlock = ({ data }: ClarifyBlockProps) => {
  const { send, isSending } = useChatSubmit();
  const isStreaming = useAgentForgeStore((s) => {
    const sid = s.activeSessionId;
    if (!sid) return false;
    const tab = s.openTabs.find((t) => t.sessionId === sid);
    return tab?.status === 'streaming' || s.streamingDraft?.sessionId === sid;
  });
  const [dismissed, setDismissed] = useState(false);
  const [pickedValue, setPickedValue] = useState<string | null>(null);
  // Already answered (hydrated transcript): show it, but lock it.
  const consumed = !!data.consumed;
  const disabled = isSending || isStreaming || consumed;

  // Substitute the picked value into the backend-supplied pending template.
  // The label rides along in parentheses so the transcript reads naturally
  // ("… to job 489 (Senior Ruby Engineer)") rather than a bare numeric id.
  const sendChoice = (value: string, label: string) => {
    if (disabled) return;
    if (isCancelOption(value, label)) {
      setDismissed(true);
      return;
    }
    const filled = data.pending.includes('{value}')
      ? data.pending.replace('{value}', `${value} (${label})`)
      : `${data.pending} ${value} (${label})`;
    setPickedValue(value);
    void send(filled, undefined, { selectionSnapshot: [] });
  };

  if (dismissed) {
    return (
      <Box
        data-test-id="agentforge-clarify-block-dismissed"
        border="1px solid"
        borderColor="border-color"
        borderRadius="10px"
        px="14px"
        py="10px"
        bg="secondary-25"
      >
        <Text variant="small" className="text-secondary-500">
          Dismissed — nothing was changed.
        </Text>
      </Box>
    );
  }

  const createOption = data.options.find((o) => isCreateOption(o.label));
  const cancelOption = data.options.find((o) =>
    isCancelOption(o.value, o.label)
  );
  const choices = data.options.filter(
    (o) => !isCreateOption(o.label) && !isCancelOption(o.value, o.label)
  );

  const onSelect = (value: string) => {
    const picked = choices.find((o) => o.value === value);
    if (picked) sendChoice(picked.value, picked.label);
  };

  return (
    <Box
      data-test-id="agentforge-clarify-block"
      bg="base-white"
      border="1px solid"
      borderColor="border-color"
      borderRadius="10px"
      overflow="hidden"
    >
      <Box
        px="14px"
        py="10px"
        borderBottom="1px solid"
        borderColor="border-color"
        bg="secondary-25"
      >
        <Text variant="label" as="span" className="text-secondary-900">
          {data.intro}
        </Text>
      </Box>

      <Flex direction="column" gap="8px" px="14px" py="12px">
        {choices.length > 0 && (
          <Box
            opacity={disabled ? 0.6 : 1}
            pointerEvents={disabled ? 'none' : undefined}
          >
            <Select
              size="sm"
              mode="single"
              aria-label={data.intro}
              placeholder="Select an option"
              optionLayout="stacked"
              value={pickedValue ?? data.answered ?? ''}
              options={choices.map(toJobSelectOption)}
              onChange={(v: string) => onSelect(v)}
            />
          </Box>
        )}

        {/* Stays visible once answered, but disabled — so the card still shows
            the action it offered rather than collapsing to nothing. */}
        {createOption && (
          <Flex align="center" gap="8px">
            <Button
              variant="quaternary"
              size="sm"
              disabled={disabled}
              leftIcon={<Plus size={16} />}
              onClick={() => sendChoice(createOption.value, createOption.label)}
              data-test-id={`agentforge-clarify-option-${createOption.value}`}
            >
              {createOption.label}
            </Button>
            {isSending && pickedValue === createOption.value && (
              <Loader size={16} />
            )}
          </Flex>
        )}

        {cancelOption && !consumed && (
          <Box>
            <Button
              variant="quaternary"
              size="sm"
              disabled={disabled}
              onClick={() => sendChoice(cancelOption.value, cancelOption.label)}
              data-test-id={`agentforge-clarify-option-${cancelOption.value}`}
            >
              {cancelOption.label}
            </Button>
          </Box>
        )}
      </Flex>

      {data.submitLabel && (
        <Box
          px="14px"
          py="8px"
          borderTop="1px solid"
          borderColor="border-color"
          bg="secondary-25"
        >
          <Text variant="small" className="text-secondary-600">
            {consumed ? 'Answered.' : data.submitLabel}
          </Text>
        </Box>
      )}
    </Box>
  );
};
